import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { settingsLabel } from '../../settingsNav';
import { Empty, ErrorNote, Spinner } from '../../components/common';
import { IconPlusCircle } from '../../components/icons';

/**
 * ตั้งค่า — one route per sidebar submenu entry.
 *
 * Layout on every page: breadcrumb, title, optional inner tabs, optional
 * create button, then the panel. Sections and tab names mirror the live POS.
 *
 * The create button on each section opens a FormModal wired to that section's
 * POST endpoint, then reloads the list.
 */
function SettingsPage({ tabs, tab, onTab, action, onAction, actionMenu, search, children }) {
  const { pathname } = useLocation();
  const title = settingsLabel(pathname);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <>
      {/* The create button belongs to the page, not the tab strip — it sits on
          the title row in the original, with the tabs and the search below. */}
      <div className="page-head">
        <div>
          {/* Two tones, as on the original: the parent crumb is muted and the
              page you are on is the darker secondary text colour. */}
          <div className="crumb">ตั้งค่า <span>•</span> <span className="now">{title}</span></div>
          <h1 className="page-title">{title}</h1>
        </div>
        {action && (
          /* Some pages create more than one kind of thing, and there the button
             asks which before any dialog opens — สร้างบริการ on setting-product
             offers บริการ and กลุ่มบริการ. */
          <div className="page-action-wrap">
            <button
              className="btn-solid page-action"
              onClick={() => (actionMenu ? setMenuOpen((o) => !o) : onAction())}
            >
              <IconPlusCircle size={16} /> {action}
            </button>
            {actionMenu && menuOpen && (
              <>
                <div className="row-menu-scrim" onMouseDown={() => setMenuOpen(false)} />
                <div className="action-menu">
                  {actionMenu.map((it) => (
                    <button
                      key={it.label}
                      onClick={() => { setMenuOpen(false); it.onClick(); }}
                    >
                      {it.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* setting-tabs, not the bare .tab-row: their settings tab is a white box
          with a purple hairline when it is the one you are on, and the app's
          other tab strip (the booking builder's) is not — see App.css. */}
      {(tabs || search) && (
        <div className="tab-row setting-tabs">
          {(tabs || []).map((t) => (
            <button
              key={t.key}
              className={`tab${tab === t.key ? ' active' : ''}`}
              onClick={() => onTab(t.key)}
            >
              {t.label}
            </button>
          ))}
          {search && <div className="tab-search">{search}</div>}
        </div>
      )}

      {children}
    </>
  );
}

function Panel({ loader, children, empty, emptyIcon }) {
  if (loader.loading) return <Spinner />;
  if (loader.error) return <ErrorNote error={loader.error} />;
  if (empty && empty(loader.data)) {
    return <div className="card"><Empty icon={emptyIcon} /></div>;
  }
  return children(loader.data);
}

const Field = ({ label, children }) => (
  <div className="field"><label>{label}</label>{children}</div>
);

/**
 * The switch their dialogs put on a settings row: name over a grey line saying
 * what off and on mean, and the control hard right. บริการ, หมอนวด and เตียง
 * all end on one of these.
 */
function Switch({ label, hint, on, onChange }) {
  return (
    <div className="svc-switch-row">
      <span>
        <span className="text-[16px]">{label}</span>
        <span className="hint" style={{ display: 'block' }}>{hint}</span>
      </span>
      <button
        type="button"
        className={`svc-switch${on ? ' on' : ''}`}
        aria-pressed={on}
        onClick={() => onChange(!on)}
      />
    </div>
  );
}

/** A labelled radio row — the ทั่วไป page is built almost entirely from these. */
function Choice({ label, value, options, onChange }) {
  return (
    <div className="choice">
      <div className="choice-label">{label}</div>
      <div className="choice-opts">
        {options.map((o) => (
          <label key={o.value} className={`radio${value === o.value ? ' on' : ''}`}>
            <input
              type="radio"
              checked={value === o.value}
              onChange={() => onChange(o.value)}
            />
            {o.label}
          </label>
        ))}
      </div>
    </div>
  );
}

/* ── ทั่วไป ───────────────────────────────────────────────────────────── */

export { SettingsPage, Panel, Choice, Field, Switch };
